import { allowsVibrantiumId, SR_METALS, SR_LABELS } from './studioConfig.js'
import { stepKeysFor, cellFor, PRODUCT_BY_ID, STYLE_BY_ID } from './productModel.js'
import { levelUsesSteps } from './levels.js'

/** Does this build's LEVEL draw the thirteen steps? Read from `levels.js` —
 *  never re-derived here, so the blocker wording and the screen cannot disagree
 *  about which level the operator is in. */
const usesSteps = (state) => levelUsesSteps(state && state.mode)

/**
 * ARTWORK STUDIO — THE PAYLOAD (pure; no React, no network, no clock).
 *
 * This module is the CONTRACT with `POST /api/artwork/render` in
 * sr-portal-backend (`src/artwork/promptAssembler.js#normalizeBuild`). Nothing
 * else in the studio composes a request body — the UI edits state, this file
 * turns state into the exact body, and `verify-studio.mjs` asserts the shape
 * against the real contract without a browser.
 *
 * THREE PROPERTIES IT ENFORCES STRUCTURALLY:
 *
 *  1. NOTHING IS INVENTED. A value the operator did not key is dropped, not
 *     defaulted. The engine has its own neutral defaults for render mechanics
 *     (metal, stone setting, views); it must never receive a fabricated title,
 *     record, audience or presentation line that reads as keyed truth.
 *  2. SUGGEST, DON'T AUTOFILL (Section 2). `suggestAttributes()` returns
 *     candidates with their source. It writes nothing — the caller applies a
 *     chip only on an explicit click. There is no code path from a suggestion
 *     to the payload that does not pass through the operator.
 *  3. THE LOCKED LAYER IS VISIBLE (Section 3). `lockedSummary()` returns
 *     exactly the strings the backend will assert survive into the prompt
 *     (`promptAssembler.js#lockedStrings` + `assertLockedIntact`), so the
 *     operator sees what is protected before submitting rather than after.
 */

const s = (v) => (v == null ? '' : String(v).trim())
const nonEmpty = (arr) => (Array.isArray(arr) ? arr.map(s).filter(Boolean) : [])

/* ------------------------------------------------------------------ *
 * Component references
 * ------------------------------------------------------------------ */

/**
 * A component ref as the engine accepts it: a string, an array, or
 * `{id,name,source,description}` (`promptAssembler.js#refText`). Anything with
 * no usable label collapses to null so the engine falls back to its own neutral
 * wording instead of receiving an empty object.
 */
export function cleanRef(ref) {
  if (ref == null) return null
  if (typeof ref === 'string' || typeof ref === 'number') return s(ref) || null
  if (Array.isArray(ref)) {
    const out = ref.map(cleanRef).filter(Boolean)
    return out.length ? out : null
  }
  const name = s(ref.name || ref.label || ref.title || ref.text || ref.id)
  if (!name) return null
  const out = { name }
  if (s(ref.id)) out.id = s(ref.id)
  if (s(ref.source)) out.source = s(ref.source)
  if (s(ref.description)) out.description = s(ref.description)
  if (s(ref.url)) out.url = s(ref.url)
  return out
}

/** How a ref reads in the UI. Mirrors the backend's refText for the summary. */
export function refLabel(ref) {
  if (ref == null) return ''
  if (typeof ref === 'string' || typeof ref === 'number') return s(ref)
  if (Array.isArray(ref)) return ref.map(refLabel).filter(Boolean).join(', ')
  return s(ref.name || ref.label || ref.title || ref.text || ref.id)
}

/* ------------------------------------------------------------------ *
 * The initial build state
 * ------------------------------------------------------------------ */

export function emptyBuild(config = {}) {
  return {
    product: 'ring',
    style: 'championship',
    mode: config.defaultMode || 'render',
    settingPreset: config.defaultSettingPreset || 'studio-black',
    setting: { sport: '', region: '', venue: '' },
    scale: config.defaultScale || 'Large',
    complexity: config.defaultComplexity || 3,
    views: [...(config.defaultViews || ['top', 'left-shank', 'right-shank', 'palm', 'exploded'])],
    championshipId: '',
    logoC: null,
    components: {
      housing: null,
      bezel: null,
      shank1: null,
      shank2: null,
      background: null,
      girdle: 'none',
      heroEmblem: '',
      coreEngraving: '',
      shankExtras: '',
      actionBezel: { mode: 'static', reveal: '', insert: '' },
      stones: { shapes: [], setting: [], package: 'genuine' },
      metal: { tone: SR_METALS[0].id, finish: 'mirror-polish', vibrantiumId: '' },
      teamColor: [],
    },
    keyed: {
      titleTop: '',
      titleBottom: '',
      names: [],
      record: '',
      shank1Text: '',
      shank2Text: '',
      palmText: '',
      signature: '',
      presentationLine: '',
      audience: '',
    },
    scripts: {},
  }
}

/* ------------------------------------------------------------------ *
 * State -> the POST body
 * ------------------------------------------------------------------ */

/**
 * WHICH PAYLOAD FIELDS EACH STEP OWNS.
 *
 * A step trimmed out of the flow must not be able to post anything, and that
 * rule belongs HERE rather than in the component — same reasoning as the
 * Vibrantium ID drop below. The state is long-lived: an operator can configure
 * a ring, pick the shanks, then switch the product to Pendant. The shank steps
 * vanish from the screen, but the values are still in state, and without this
 * the build would post two side-shank designs and their keyed text on a piece
 * that has no sides. The engine would not refuse it — it does not validate the
 * product at all — so it would render them.
 *
 * Only the trimmable steps appear here. The universal four (logoC, title,
 * complexity, background) are on every build of every product.
 */
const STEP_FIELDS = {
  bezel: { components: ['bezel', 'heroEmblem'] },
  girdle: { components: ['girdle'] },
  shank1: { components: ['shank1', 'shankExtras'], keyed: ['shank1Text'] },
  shank2: { components: ['shank2'], keyed: ['shank2Text'] },
  palm: { keyed: ['palmText'] },
  core: { components: ['coreEngraving'], keyed: ['signature'] },
  stones: { components: ['stones'] },
  metal: { components: ['metal'] },
  scale: { top: ['scale'] },
}

/**
 * Strip everything the trimmed flow never asked for. Pure, and total: an
 * unknown product returns the full step set, so this can only ever remove
 * fields the model positively says the piece does not have.
 */
export function dropTrimmed(payload, productId, styleId) {
  const keep = new Set(stepKeysFor(productId, styleId))
  const out = { ...payload, components: { ...payload.components }, keyed: { ...payload.keyed } }
  for (const [key, fields] of Object.entries(STEP_FIELDS)) {
    if (keep.has(key)) continue
    for (const f of fields.components || []) delete out.components[f]
    for (const f of fields.keyed || []) delete out.keyed[f]
    for (const f of fields.top || []) delete out[f]
  }
  // The action bezel rides on the bezel step; with no bezel there is nothing
  // for it to be an action ON, so the always-declared rule stops applying.
  if (!keep.has('bezel')) delete out.components.actionBezel
  return out
}

/**
 * The exact body posted to `POST /api/artwork/render`.
 * Empty values are OMITTED rather than sent blank — the engine's own defaults
 * are better than an empty string, and a dropped key can never read as keyed.
 */
export function buildPayload(state) {
  const st = state || {}
  const c = st.components || {}
  const k = st.keyed || {}
  const stones = c.stones || {}
  const metal = c.metal || {}
  const ab = c.actionBezel || {}

  const components = {}
  const put = (key, val) => { if (val !== null && val !== undefined && val !== '') components[key] = val }

  put('housing', cleanRef(c.housing))
  put('bezel', cleanRef(c.bezel))
  put('shank1', cleanRef(c.shank1))
  put('shank2', cleanRef(c.shank2))
  put('background', cleanRef(c.background))
  put('heroEmblem', cleanRef(c.heroEmblem))
  put('coreEngraving', cleanRef(c.coreEngraving))
  put('shankExtras', cleanRef(c.shankExtras))

  // 'none' is a real answer, not a blank — the engine renders a clean metal
  // girdle for it, so it is sent rather than dropped.
  if (s(c.girdle)) components.girdle = s(c.girdle)

  const teamColor = nonEmpty(c.teamColor)
  if (teamColor.length) components.teamColor = teamColor

  const shapes = nonEmpty(stones.shapes)
  const setting = nonEmpty(stones.setting)
  const pkg = s(stones.package)
  if (shapes.length || setting.length || pkg) {
    components.stones = {}
    if (shapes.length) components.stones.shapes = shapes
    if (setting.length) components.stones.setting = setting
    if (pkg) components.stones.package = pkg
  }

  const tone = s(metal.tone)
  const finish = s(metal.finish)
  // A Vibrantium ID is a custom color for a VIBRANTIUM piece. Solid gold is the
  // color it is, so an id left over from an earlier pick is dropped here rather
  // than posted — otherwise switching Vibrantium → 14K Yellow Gold silently
  // sends the engine a color override for a metal that cannot take one. The
  // rule lives in the contract, not the component, so it holds no matter how
  // the state got that way.
  const vibrantiumId = allowsVibrantiumId(tone) ? s(metal.vibrantiumId) : ''
  if (tone || finish || vibrantiumId) {
    components.metal = {}
    if (tone) components.metal.tone = tone
    if (finish) components.metal.finish = finish
    if (vibrantiumId) components.metal.vibrantiumId = vibrantiumId
  }

  // The action bezel is ALWAYS declared. `static` is a positive instruction to
  // the beast ("no opening or removable mechanism"), not an absence.
  const abMode = s(ab.mode) || 'static'
  components.actionBezel = { mode: abMode }
  if (abMode !== 'static') {
    if (s(ab.reveal)) components.actionBezel.reveal = s(ab.reveal)
    if (s(ab.insert)) components.actionBezel.insert = s(ab.insert)
  }

  const keyed = {}
  const putKeyed = (key, val) => { if (s(val)) keyed[key] = s(val) }
  putKeyed('titleTop', k.titleTop)
  putKeyed('titleBottom', k.titleBottom)
  const names = nonEmpty(k.names)
  if (names.length) keyed.names = names
  putKeyed('record', k.record)
  putKeyed('shank1Text', k.shank1Text)
  putKeyed('shank2Text', k.shank2Text)
  putKeyed('palmText', k.palmText)
  putKeyed('signature', k.signature)
  putKeyed('presentationLine', k.presentationLine)
  putKeyed('audience', k.audience)

  const scripts = {}
  for (const [key, val] of Object.entries(st.scripts || {})) {
    if (s(val)) scripts[key] = s(val)
  }

  const setting0 = st.setting || {}
  const settingOut = {}
  for (const key of ['sport', 'region', 'venue']) {
    if (s(setting0[key])) settingOut[key] = s(setting0[key])
  }

  const payload = {
    product: s(st.product) || 'ring',
    style: s(st.style) || 'championship',
    mode: s(st.mode) || 'render',
    settingPreset: s(st.settingPreset) || 'studio-black',
    scale: s(st.scale) || 'Large',
    complexity: Number(st.complexity) || 3,
    components,
    logoC: cleanRef(st.logoC),
    keyed,
    scripts,
  }
  if (Object.keys(settingOut).length) payload.setting = settingOut
  const views = nonEmpty(st.views)
  if (views.length) payload.views = views
  if (s(st.championshipId)) payload.championshipId = s(st.championshipId)

  return dropTrimmed(payload, payload.product, payload.style)
}

/* ------------------------------------------------------------------ *
 * Validation — problems BLOCK, warnings do not
 * ------------------------------------------------------------------ */

/**
 * `problems` mirror the backend's own `validateBuild()` exactly, so the studio
 * never posts a build the engine will 400. `warnings` are the Locked Layer and
 * spec expectations the engine does NOT enforce — surfaced so the operator
 * decides, never silently fixed.
 */
export function validateStudio(state, config = {}) {
  const payload = buildPayload(state)
  const problems = []
  const warnings = []

  const styles = (config.styles || []).map((x) => String(x.style || x.id || x))
  if (styles.length && !styles.includes(payload.style)) {
    problems.push(`No beast prompt for style "${payload.style}" — the engine accepts: ${styles.join(', ')}.`)
  }

  /* ⚠️ THIS PROBLEM IS OURS, NOT THE ENGINE'S — and it is here because the
   * engine's SILENCE is the defect. `promptAssembler.js#normalizeBuild` takes
   * `product` as free text and never checks it, so a pendant build against the
   * ring beast returns 200 with five views of a ring captioned "CHAMPIONSHIP
   * PENDANT". A 400 would be recoverable; a plausible wrong render is not,
   * because nothing downstream can tell it was wrong. So the studio refuses the
   * cell the beast cannot make, and names the missing artefact.
   *
   * The chips are already disabled for these combinations — this is the second
   * half of the same rule, in the contract, so it holds however the state got
   * that way (a `?flow=` seed, a restored draft, a future deep link). */
  const cell = cellFor(payload.product, payload.style, config)
  if (!cell.available && !(styles.length && !styles.includes(payload.style))) {
    const p = PRODUCT_BY_ID[payload.product]
    const st = STYLE_BY_ID[payload.style]
    problems.push(
      `${(p && p.label) || payload.product} × ${(st && st.label) || payload.style} cannot be rendered yet — ${cell.reason}`
    )
  }
  const presets = (config.settingPresets || []).map((p) => p.id)
  if (presets.length && !presets.includes(payload.settingPreset)) {
    problems.push(`Unknown Setting & Output preset "${payload.settingPreset}".`)
  }
  /* The step NUMBER is read off the trimmed flow, not hardcoded. A pendant
   * drops the girdle and both shanks, so its title step is number 3 — telling
   * the operator to go to "step 4" would send them to a step that is not
   * there. */
  const flow = stepKeysFor(payload.product, payload.style)
  const stepNo = (key) => {
    const i = flow.indexOf(key)
    return i >= 0 ? `Step ${i + 1}` : 'A required step'
  }
  /* ⚠️ THE BLOCKER MUST SPEAK THE LEVEL'S OWN LANGUAGE.
   *
   * Brian, on the live site: *"generated concept should not reference a high
   * school. i shouldnt have to select certain items. Blocked — the engine would
   * refuse this build / Step 4 — a title is required / Step 1 — the bezel logo
   * (C) is required. The placeholder logo has nothing to be replaced with."*
   *
   * He was right and this was the defect. Level 2 draws NO steps — so "Step 4"
   * and "Step 1" name controls that are not on the screen, and "the placeholder
   * logo has nothing to be replaced with" describes a picker the operator was
   * promised they would never have to touch. The messages were rendered before
   * he had typed anything, which read as the level being broken.
   *
   * THE REQUIREMENTS ARE NOT WEAKENED — a title and an owned crest are locked-
   * layer inputs and the engine refuses a build without them at either level.
   * What changes is WHOSE JOB IT IS: at level 1 the operator answers a step; at
   * level 2 the system sources it, so an absence is a SOURCING outcome and the
   * remedy is to source, correct or pick — never to go and fill in step 1. */
  const generated = !usesSteps(state)

  if (!payload.keyed.titleTop) {
    problems.push(generated
      ? 'Nothing sourced yet — describe the job in one line and press “Source the build”. The title comes off the championship record.'
      : `${stepNo('title')} — a title is required. It is keyed type, never invented by the AI.`)
  }
  if (!payload.logoC) {
    problems.push(generated
      ? 'No owned crest resolved yet — the concept sources it from the school record. Source the build, then pick the school on the receipt if it landed on the wrong one.'
      : `${stepNo('logoC')} — the bezel logo (C) is required. The placeholder logo has nothing to be replaced with.`)
  }

  if (!payload.keyed.presentationLine) {
    warnings.push('No presentation line. It defaults from the championship and school records once both are picked, so an empty one means neither is known yet or it was cleared by hand. The Locked Layer expects "School – Sport – Championship – Year".')
  }
  if (!payload.keyed.audience) {
    warnings.push('No audience — the Locked Layer expects "presented to …" on every presentation.')
  }
  const preset = (config.settingPresets || []).find((p) => p.id === payload.settingPreset)
  const slots = (preset && preset.slots) || []
  if (slots.includes('VENUE') && !(payload.setting && (payload.setting.venue || payload.setting.sport || payload.setting.region))) {
    warnings.push('Contextual Venue is selected but no sport, region or venue is set — the engine will fall back to a generic championship venue.')
  }
  if (slots.includes('BACKGROUND') && !payload.components.background) {
    warnings.push('Presentation Background is selected but no background was picked in step 13.')
  }
  if (!payload.views || !payload.views.length) {
    warnings.push('No views selected — the engine will render its default five-view set.')
  }

  return { problems, warnings, ok: problems.length === 0, payload }
}

/* ------------------------------------------------------------------ *
 * The Locked Layer, surfaced (Section 3)
 * ------------------------------------------------------------------ */

/**
 * Exactly what the backend will hold the render to. Mirrors
 * `promptAssembler.js#lockedStrings` plus the two marks that are always on.
 */
export function lockedSummary(state) {
  const k = (state && state.keyed) || {}
  const strings = []
  const push = (label, value) => { if (s(value)) strings.push({ label, value: s(value) }) }
  push('Title (top)', k.titleTop)
  push('Title (bottom)', k.titleBottom)
  nonEmpty(k.names).forEach((n) => push('Name', n))
  push('Record', k.record)
  push('Side shank 1 text', k.shank1Text)
  push('Side shank 2 text', k.shank2Text)
  push('Palm / interior', k.palmText)
  push('Signature', k.signature)
  push('Presentation line', k.presentationLine)
  push('Presented to', k.audience)

  const logo = refLabel(state && state.logoC)
  return {
    strings,
    logoC: logo,
    marks: [
      { label: "SR maker's mark", detail: 'Present on every build, preliminary renders included.', ok: true },
      {
        label: 'Client logo C',
        detail: logo
          ? `${logo} — reproduced faithfully from the real crest, never distorted or re-drawn.`
          : 'Not selected yet — required before the build can be recorded.',
        ok: !!logo,
      },
      {
        label: 'Keyed type',
        detail: strings.length
          ? `${strings.length} string${strings.length === 1 ? '' : 's'} rendered verbatim; the backend refuses a prompt that dropped one.`
          : 'Nothing keyed yet — the title is required.',
        ok: strings.length > 0,
      },
    ],
  }
}

/* ------------------------------------------------------------------ *
 * SUGGEST, DON'T AUTOFILL (Section 2)
 * ------------------------------------------------------------------ */

const YEAR_RE = /\b(19|20)\d{2}\b/

/**
 * Candidates drawn from the championship record, the school record and the
 * universe. Each carries the FIELD it would fill and WHERE it came from.
 * This function writes nothing: the caller applies a chip on an explicit click.
 *
 * @returns {Array<{id,label,value,field,source}>}
 */
export function suggestAttributes({ championship = null, school = null } = {}) {
  const out = []
  const seen = new Set()
  const add = (field, label, value, source) => {
    const v = s(value)
    if (!v) return
    const id = `${field}:${v}`
    if (seen.has(id)) return
    seen.add(id)
    out.push({ id, field, label, value: v, source })
  }

  const ch = championship || {}
  const sc = school || {}
  const chName = s(ch.name || ch.championship || ch.title)
  const year = s(ch.year) || (YEAR_RE.exec(chName) || [])[0] || ''

  // Title candidates — the operator writes the exact title, these are starts.
  add('titleTop', 'Championship', chName, 'championship record')
  if (s(ch.sport)) add('titleTop', 'Sport', s(ch.sport).toUpperCase(), 'championship record')
  add('titleBottom', 'Year', year, 'championship record')
  if (s(ch.level)) add('titleBottom', 'Level', ch.level, 'championship record')
  add('titleBottom', 'Champions', 'CHAMPIONS', 'SR convention')

  // Names / verbatim attributes.
  add('names', 'School', s(sc.name) || s(ch.school), 'HubSpot company')
  add('names', 'Champion', s(ch.champion || ch.current_champion), 'championship record')
  add('names', 'Mascot', s(sc.mascot || ch.mascot), 'HubSpot company')
  add('names', 'Head coach', s(ch.coach || ch.headCoach), 'coaches directory')
  add('names', 'Conference', s(ch.conference), 'championship record')

  add('record', 'Season record', s(ch.record || ch.seasonRecord), 'scrape / universe')

  add('shank1Text', 'Mascot + year', [s(sc.mascot || ch.mascot), year].filter(Boolean).join(' '), 'derived')
  add('shank2Text', 'School + sport', [s(sc.name) || s(ch.school), s(ch.sport)].filter(Boolean).join(' ').toUpperCase(), 'derived')

  add('audience', 'Athletic director', s(ch.owner || ch.audience), 'championship record')

  const line = presentationLineSuggestion({ championship, school })
  add('presentationLine', 'Presentation line', line, 'derived — School – Sport – Championship – Year')

  return out
}

/**
 * The Locked Layer's presentation line (Section 4, step 12):
 * "School – Sport – Championship – Year". Returns '' when there is not enough
 * to compose one — a half-built line is worse than none.
 */
export function presentationLineSuggestion({ championship = null, school = null } = {}) {
  const ch = championship || {}
  const sc = school || {}
  const chName = s(ch.name || ch.championship || ch.title)
  const year = s(ch.year) || (YEAR_RE.exec(chName) || [])[0] || ''
  // The championship's own name usually already carries the year; the line
  // states it once, at the end.
  const title = year ? chName.replace(year, '').replace(/\s{2,}/g, ' ').trim() : chName
  const parts = [s(sc.name) || s(ch.school), s(ch.sport), title, year].map(s).filter(Boolean)
  return parts.length >= 2 ? parts.join(' – ') : ''
}

/**
 * THE PRESENTATION LINE IS DEFAULTED, NOT ASKED FOR.
 *
 * Brian, 2026-09-16: it was a required human input with no default, even though
 * every field it needs is already derived from the records the operator has
 * picked. Step 13 offered it as a chip nobody had to click, so the commonest
 * outcome was a build with no presentation line and a warning nobody could act
 * on without retyping what the record already said.
 *
 * IT STILL NEVER OVERWRITES A PERSON. `lastAuto` is the line THIS function put
 * there last time; anything else in the field is the operator's own wording and
 * is left alone. That keeps propose-don't-overwrite intact where it matters (a
 * human's words), while dropping the ceremony where it does not (a line
 * composed from School, Sport, Championship and Year, which the operator can
 * edit in place).
 *
 * Returns the line to write, or null for "leave the field alone". Pure: it
 * writes nothing itself, so the caller is still the only thing that can.
 */
export function presentationLineDefault(current, lastAuto, { championship = null, school = null } = {}) {
  const next = presentationLineSuggestion({ championship, school })
  // Nothing to compose from: never blank out what is already there.
  if (!next) return null
  const cur = s(current)
  // Typed by hand, or edited after we filled it. The person wins.
  if (cur && cur !== s(lastAuto)) return null
  if (cur === next) return null
  return next
}

/** The sport / region the Contextual Venue preset suggests from (Section 8A). */
export function venueContext({ championship = null, school = null } = {}) {
  const ch = championship || {}
  const sc = school || {}
  return {
    sport: s(ch.sport),
    region: s(ch.state || ch.location || sc.state || sc.location),
  }
}

/* ------------------------------------------------------------------ *
 * Reading the render response
 * ------------------------------------------------------------------ */

/**
 * What `POST /api/artwork/render` actually came back with.
 *
 * `renderPending: true` is a COMPLETE, SUCCESSFUL state — the build record was
 * written and every view's prompt was assembled with the locked layer asserted
 * intact; only the image call was skipped, because OPENAI_API_KEY lives on
 * Railway. The UI must show the prompts and say the images render on deploy.
 * Treating it as an error would throw away a real build record.
 *
 * A per-view `error` is likewise scoped to THAT view: the other views still
 * rendered and are still worth showing.
 */
export function readRenderResult(res) {
  const r = res || {}
  const images = Array.isArray(r.images) ? r.images : []
  const prompts = Array.isArray(r.prompts) ? r.prompts : []
  const rendered = images.filter((i) => i && (i.url || i.hostedUrl))
  const hosted = images.filter((i) => i && i.hostedUrl)
  const failed = images.filter((i) => i && i.error)
  return {
    ok: !!r.buildId,
    buildId: r.buildId || null,
    pending: r.renderPending === true,
    // A pending build is NOT an error and NOT empty — it has prompts.
    isError: false,
    promptCount: prompts.length,
    viewCount: images.length,
    renderedCount: rendered.length,
    hostedCount: hosted.length,
    failedViews: failed.map((i) => ({ view: i.view, label: i.label, error: i.error })),
    status: (r.record && r.record.status) || 'draft',
  }
}

/**
 * Has the engine FINISHED with this build, images or not?
 *
 * With image spend switched off the detached render ends by leaving
 * renderPending TRUE with a note starting "render pending:", permanently. The
 * poller used to wait on renderPending alone, so it spun for 7.5 minutes on a
 * build that had settled in two seconds, then said "still rendering". That
 * is most of what "I only got a prompt" looked like. The note is the signal.
 */
export function renderSettled(record) {
  const r = record || {}
  if (r.renderPending !== true) return true
  return (Array.isArray(r.notes) ? r.notes : []).some((n) => /^(render pending:|render_aborted:)/.test(String(n)))
}

/** The engine's own words for why a build has no images, or ''. */
export function pendingReason(record) {
  const n = (Array.isArray(record?.notes) ? record.notes : []).find((x) => /^render pending:/.test(String(x)))
  return n ? String(n).replace(/^render pending:\s*/, '') : ''
}

/* ------------------------------------------------------------------ *
 * Progress — how far through the paint-by-numbers flow this build is
 * ------------------------------------------------------------------ */

/** Has this step been answered? Drives the progress rail's ticks. */
export function stepDone(state, step) {
  const st = state || {}
  const c = st.components || {}
  const k = st.keyed || {}
  switch (step.key) {
    case 'logoC': return !!cleanRef(st.logoC)
    case 'bezel': return !!cleanRef(c.bezel)
    case 'girdle': return !!s(c.girdle)
    case 'title': return !!s(k.titleTop)
    case 'shank1': return !!(cleanRef(c.shank1) || s(k.shank1Text))
    case 'shank2': return !!(cleanRef(c.shank2) || s(k.shank2Text))
    case 'palm': return !!s(k.palmText)
    case 'core': return !!(cleanRef(c.coreEngraving) || s(k.signature))
    case 'stones': return !!(nonEmpty((c.stones || {}).shapes).length || nonEmpty((c.stones || {}).setting).length)
    case 'metal': return !!s((c.metal || {}).tone)
    case 'scale': return !!s(st.scale)
    case 'complexity': return !!Number(st.complexity)
    case 'background': return !!(cleanRef(c.background) || s(k.presentationLine))
    default: return !!s((st.scripts || {})[step.key])
  }
}

/**
 * THE ONE-LINE ANSWER a collapsed step shows.
 *
 * The studio is an ACCORDION — on a phone, thirteen fully-expanded steps is one
 * endless scroll and no way to see where you are. A collapsed step is only
 * honest if it still says what it holds, so this returns exactly what was
 * supplied, in the operator's own words, and **''** when nothing was. It is
 * pure and lives beside `stepDone` for the same reason: the header and the
 * progress rail must never be able to disagree.
 */
export function stepSummary(state, step) {
  const st = state || {}
  const c = st.components || {}
  const k = st.keyed || {}
  const join = (...parts) => parts.map(s).filter(Boolean).join(' · ')
  const stones = c.stones || {}
  const metal = c.metal || {}

  switch (step.key) {
    case 'logoC': return refLabel(st.logoC)
    case 'bezel': return join(refLabel(c.bezel), c.heroEmblem)
    case 'girdle': return s(c.girdle)
    case 'title': return join(k.titleTop, k.titleBottom, k.record)
    case 'shank1': return join(refLabel(c.shank1), k.shank1Text)
    case 'shank2': return join(refLabel(c.shank2), k.shank2Text)
    case 'palm': return s(k.palmText)
    case 'core': return join(refLabel(c.coreEngraving), k.signature)
    case 'stones': return join(nonEmpty(stones.shapes).join(', '), nonEmpty(stones.setting).join(', '), stones.package)
    // The metal's id IS its name, so it reads back as-is. The FINISH id is the
    // engine's own key (`mirror-polish`), so it is rendered through SR_LABELS —
    // a collapsed step must read the way the open step did, not leak the key.
    case 'metal': return join(
      metal.tone,
      metal.finish && (SR_LABELS.metalFinishes[metal.finish] || metal.finish),
      allowsVibrantiumId(metal.tone) ? metal.vibrantiumId : '',
    )
    case 'scale': return s(st.scale)
    case 'complexity': return st.complexity ? String(st.complexity) : ''
    case 'background': return join(refLabel(c.background), k.presentationLine)
    default: return s((st.scripts || {})[step.key])
  }
}
